import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/core/Badge';
import { IconButton } from '@/components/core/IconButton';

import { sentenceCase, UserGroup } from './user-groups.types';

interface ColumnsProps {
    t: (key: string) => string;
    onEdit: (groupId: number | string) => void;
    onDelete: (group: UserGroup) => void;
}

export const columns = ({ t, onEdit, onDelete }: ColumnsProps): ColumnDef<UserGroup>[] => ([
    {
        accessorKey: 'groupName',
        header: sentenceCase(t('USER_GROUP.GROUP_NAME')),
        meta: {
            cellClassName: 'tw-text-center tw-text-t-sm lg:tw-text-t-base tw-text-t-primary',
        },
        cell: ({ row }) => (
            <span>{row.original.groupName}</span>
        ),
    },
    {
        id: 'userIds',
        accessorFn: (group) => group.userIds?.length ?? 0,
        header: sentenceCase(t('USER_GROUP.USER_COUNT')),
        meta: {
            cellClassName: 'tw-text-center tw-text-t-sm lg:tw-text-t-base',
        },
        cell: ({ row }) => (
            <span>{row.original.userIds?.length ?? 0}</span>
        ),
    },
    {
        id: 'roles',
        header: sentenceCase(t('USER_MANAGEMENT.ROLES')),
        enableSorting: false,
        meta: {
            cellClassName: 'tw-text-center',
        },
        cell: ({ row }) => (
            <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-justify-center tw-gap-1">
                {(row.original.roles ?? []).map((role) => (
                    <Badge key={role} variant="neutral">{role}</Badge>
                ))}
            </div>
        ),
    },
    {
        id: 'actions',
        header: sentenceCase(t('ACTIONS')),
        size: 130,
        enableSorting: false,
        enableResizing: false,
        meta: {
            pinned: true,
        },
        cell: ({ row }) => (
            <div className="tw-flex tw-h-10 tw-max-h-10 tw-items-center tw-space-x-1 tw-justify-end">
                <IconButton
                    type="button"
                    icon={PencilIcon}
                    size="md"
                    className="tw-icon-primary"
                    title={sentenceCase(t('USER_GROUP.EDIT_GROUP'))}
                    aria-label={sentenceCase(t('USER_GROUP.EDIT_GROUP'))}
                    onClick={() => {
                        if (row.original.id != null) {
                            onEdit(row.original.id);
                        }
                    }}
                />
                <IconButton
                    type="button"
                    icon={TrashIcon}
                    size="md"
                    className="tw-icon-primary"
                    title={sentenceCase(t('USER_GROUP.DELETE_GROUP'))}
                    aria-label={sentenceCase(t('USER_GROUP.DELETE_GROUP'))}
                    onClick={() => onDelete(row.original)}
                />
            </div>
        ),
    },
]);
